import React from "react";
import { Button, Chip, Grid, Typography } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";

import WorkOutlineIcon from "@material-ui/icons/WorkOutline";
import SchoolOutlinedIcon from "@material-ui/icons/SchoolOutlined";
import MemoryIcon from "@material-ui/icons/Memory";
import GetAppOutlinedIcon from "@material-ui/icons/GetAppOutlined";

import Title from "../components/title";
import { getTranslate } from "../localization";

const useStyles = makeStyles((theme) => ({
  root: {
    width: "100%",
    minHeight: "100vh",
    padding: "52px 44px 90px",
    direction: theme.direction,
    textAlign: theme.direction === "rtl" ? "right" : "left",
    [theme.breakpoints.down("sm")]: { padding: "34px 22px 64px" },
    [theme.breakpoints.down("xs")]: { padding: "28px 16px 56px" },
  },
  inner: {
    width: "min(100%, 1280px)",
    margin: "0 auto",
  },
  titleRow: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: 20,
    direction: theme.direction,
    [theme.breakpoints.down("xs")]: {
      flexDirection: "column",
      alignItems: "stretch",
      gap: 0,
    },
  },
  downloadButton: {
    minHeight: 44,
    flexShrink: 0,
    padding: "8px 16px",
    marginTop: 3,
    borderRadius: 11,
    color: "#dce7f5",
    fontSize: 11,
    fontWeight: 800,
    border: "1px solid rgba(148,163,184,.16)",
    background: "rgba(255,255,255,.022)",
    "&:hover": {
      color: "#fff",
      borderColor: "rgba(22,139,255,.35)",
      background: "rgba(22,139,255,.06)",
      transform: "translateY(-2px)",
    },
    [theme.breakpoints.down("xs")]: {
      marginBottom: 34,
    },
  },
  buttonInner: {
    display: "inline-flex",
    alignItems: "center",
    gap: 9,
    direction: theme.direction,
  },
  section: {
    marginBottom: 56,
  },
  heading: {
    display: "flex",
    alignItems: "center",
    gap: 11,
    marginBottom: 24,
    direction: theme.direction,
  },
  headingIcon: {
    flexShrink: 0,
    color: "#91a2ba",
    fontSize: 30,
  },
  headingText: {
    color: "#f7f9fc",
    fontWeight: 850,
    fontSize: "clamp(1.45rem,2.4vw,2rem)",
    textAlign: theme.direction === "rtl" ? "right" : "left",
  },
  timeline: {
    position: "relative",
    paddingLeft: theme.direction === "rtl" ? 0 : 38,
    paddingRight: theme.direction === "rtl" ? 38 : 0,
    "&::before": {
      content: '""',
      position: "absolute",
      top: 18,
      bottom: 18,
      width: 2,
      left: theme.direction === "rtl" ? "auto" : 7,
      right: theme.direction === "rtl" ? 7 : "auto",
      borderRadius: 999,
      background: "linear-gradient(rgba(64,101,166,.56),rgba(64,101,166,.10))",
    },
  },
  timelineItem: {
    position: "relative",
    marginBottom: 18,
    "&:last-child": { marginBottom: 0 },
    "&:hover $marker": {
      borderColor: "#2698ff",
      boxShadow: "0 0 0 5px rgba(22,139,255,.07),0 0 20px rgba(22,139,255,.20)",
    },
  },
  marker: {
    position: "absolute",
    top: 24,
    width: 16,
    height: 16,
    left: theme.direction === "rtl" ? "auto" : -38,
    right: theme.direction === "rtl" ? -38 : "auto",
    borderRadius: "50%",
    background: "#071022",
    border: "3px solid rgba(78,104,165,.72)",
    transition: "border-color .2s ease, box-shadow .2s ease",
  },
  experienceCard: {
    position: "relative",
    minWidth: 0,
    padding: "20px 22px",
    borderRadius: 18,
    overflow: "hidden",
    background: "linear-gradient(145deg,rgba(14,25,48,.90),rgba(8,16,31,.96))",
    border: "1px solid rgba(101,121,165,.15)",
    boxShadow: "0 12px 30px rgba(0,0,0,.13)",
    transition: "transform .22s ease, border-color .22s ease, box-shadow .22s ease",
    "&:hover": {
      transform: "translateY(-3px)",
      borderColor: "rgba(22,139,255,.27)",
      boxShadow: "0 18px 38px rgba(0,0,0,.20)",
    },
    [theme.breakpoints.down("xs")]: {
      padding: "17px 16px",
    },
  },
  date: {
    display: "inline-flex",
    alignItems: "center",
    padding: "5px 9px",
    marginBottom: 10,
    borderRadius: 999,
    color: "#7fbfff",
    fontSize: 10,
    fontWeight: 750,
    lineHeight: 1.4,
    background: "rgba(22,139,255,.055)",
    border: "1px solid rgba(22,139,255,.13)",
  },
  itemTitle: {
    color: "#2799ff",
    fontWeight: 850,
    fontSize: "clamp(1.1rem,2vw,1.45rem)",
    lineHeight: 1.5,
    marginBottom: 7,
    textAlign: theme.direction === "rtl" ? "right" : "left",
    overflowWrap: "break-word",
  },
  subtitle: {
    color: "#edf2f8",
    fontWeight: 750,
    fontSize: 13,
    lineHeight: 1.65,
    marginBottom: 7,
  },
  content: {
    color: "#929fb4",
    fontSize: 13,
    lineHeight: 1.95,
    margin: 0,
    textAlign: theme.direction === "rtl" ? "right" : "left",
    overflowWrap: "break-word",
  },
  researchCard: {
    position: "relative",
    padding: 26,
    borderRadius: 22,
    overflow: "hidden",
    background: "linear-gradient(145deg,rgba(9,31,58,.95),rgba(8,18,35,.97))",
    border: "1px solid rgba(22,139,255,.20)",
    boxShadow: "0 17px 42px rgba(0,0,0,.18)",
    [theme.breakpoints.down("xs")]: { padding: 18 },
  },
  researchTitle: {
    color: "#f7f9fc",
    fontWeight: 850,
    fontSize: "clamp(1.25rem,2.1vw,1.7rem)",
    marginBottom: 11,
    lineHeight: 1.5,
    textAlign: theme.direction === "rtl" ? "right" : "left",
  },
  researchDescription: {
    color: "#98a6ba",
    lineHeight: 1.95,
    fontSize: 13,
    marginBottom: 18,
    maxWidth: 960,
  },
  researchTags: {
    display: "flex",
    flexWrap: "wrap",
    gap: 7,
    marginBottom: 22,
  },
  chip: {
    color: "#b3c3d7",
    background: "rgba(22,139,255,.055)",
    border: "1px solid rgba(22,139,255,.15)",
    fontSize: 10,
  },
  stats: {
    display: "grid",
    gridTemplateColumns: "repeat(4,minmax(0,1fr))",
    gap: 10,
    [theme.breakpoints.down("sm")]: { gridTemplateColumns: "repeat(2,minmax(0,1fr))" },
    [theme.breakpoints.down("xs")]: { gridTemplateColumns: "1fr" },
  },
  stat: {
    padding: 15,
    minWidth: 0,
    borderRadius: 14,
    background: "rgba(255,255,255,.022)",
    border: "1px solid rgba(148,163,184,.10)",
  },
  statValue: {
    color: "#2b99ff",
    fontSize: 18,
    fontWeight: 850,
    marginBottom: 4,
    overflowWrap: "anywhere",
  },
  statLabel: {
    color: "#7f8da4",
    fontSize: 10,
    lineHeight: 1.65,
  },
  skillCard: {
    height: "100%",
    padding: 21,
    borderRadius: 19,
    background: "linear-gradient(145deg,rgba(14,25,48,.90),rgba(8,16,31,.96))",
    border: "1px solid rgba(101,121,165,.15)",
  },
  skillTitle: {
    color: "#f4f7fb",
    fontWeight: 800,
    marginBottom: 15,
    textAlign: theme.direction === "rtl" ? "right" : "left",
  },
  chipWrap: {
    display: "flex",
    flexWrap: "wrap",
    gap: 8,
  },
}));

export default function ResumeScreen() {
  const classes = useStyles();
  const translate = getTranslate();

  const renderTimeline = (items) => (
    <div className={classes.timeline}>
      {items.map((item) => (
        <div className={classes.timelineItem} key={item.id}>
          <span className={classes.marker} aria-hidden="true" />
          <article className={classes.experienceCard}>
            <div className={classes.date}>{item.date}</div>
            <Typography component="h3" className={classes.itemTitle}>{item.title}</Typography>
            <div className={classes.subtitle}>{item.subtitle}</div>
            <p className={classes.content}>{item.content}</p>
          </article>
        </div>
      ))}
    </div>
  );

  return (
    <div className={`${classes.root} modern-page`}>
      <div className={classes.inner}>
        <div className={classes.titleRow}>
          <Title title={translate.resumePageTitle} eyebrow={translate.resumeEyebrow} />
          <Button component="a" href="/Mehdi_Talebi_Resume.pdf" download className={classes.downloadButton}>
            <span className={classes.buttonInner}><span>{translate.downloadcv}</span><GetAppOutlinedIcon fontSize="small" /></span>
          </Button>
        </div>

        <section className={classes.section}>
          <div className={classes.heading}>
            <WorkOutlineIcon className={classes.headingIcon} />
            <Typography component="h2" className={classes.headingText}>{translate.workingexperience}</Typography>
          </div>
          {renderTimeline(translate.workingSteps)}
        </section>

        <section className={classes.section}>
          <div className={classes.heading}>
            <SchoolOutlinedIcon className={classes.headingIcon} />
            <Typography component="h2" className={classes.headingText}>{translate.educationalqualifications}</Typography>
          </div>
          {renderTimeline(translate.educationalSteps)}
        </section>

        <section className={classes.section}>
          <div className={classes.heading}>
            <MemoryIcon className={classes.headingIcon} />
            <Typography component="h2" className={classes.headingText}>{translate.researchSpotlight}</Typography>
          </div>

          <div className={classes.researchCard}>
            <Typography component="h3" className={classes.researchTitle}>{translate.researchTitle}</Typography>
            <div className={classes.researchDescription}>{translate.researchDescription}</div>

            <div className={classes.researchTags}>
              {translate.researchTags.map((tag) => (
                <Chip key={tag} label={tag} variant="outlined" className={classes.chip} />
              ))}
            </div>

            <div className={classes.stats}>
              {translate.researchStats.map((item) => (
                <div className={classes.stat} key={item.label}>
                  <div className={classes.statValue}>{item.value}</div>
                  <div className={classes.statLabel}>{item.label}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className={classes.section}>
          <div className={classes.heading}>
            <MemoryIcon className={classes.headingIcon} />
            <Typography component="h2" className={classes.headingText}>{translate.myskills}</Typography>
          </div>

          <Grid container spacing={2}>
            {translate.skillGroups.map((group) => (
              <Grid item xs={12} md={6} key={group.title}>
                <div className={classes.skillCard}>
                  <Typography variant="h5" className={classes.skillTitle}>{group.title}</Typography>
                  <div className={classes.chipWrap}>
                    {group.items.map((item) => (
                      <Chip variant="outlined" className={classes.chip} label={item} key={item} />
                    ))}
                  </div>
                </div>
              </Grid>
            ))}
          </Grid>
        </section>
      </div>
    </div>
  );
}
